import { ROUTES } from "../constants";
import { apiRequest } from "./api.client";

export { ApiError as AllergyApiError } from "./api.errors";

// ── Types ──────────────────────────────────────────────────────────────────────

export type AllergySeverity = "Mild" | "Moderate" | "Severe" | "Life-Threatening";
export type AllergyReactionType = "Drug" | "Food" | "Environmental" | "Contact";

export interface AllergyResponse {
  allergy_id: string;
  patient_id: string;
  allergen: string;
  severity: AllergySeverity;
  reaction_type?: AllergyReactionType | null;
  reaction?: string | null;
  verified_date?: string | null;
  notes?: string | null;
  created_at: string;
  is_life_threatening: boolean;
}

export interface AllergyCreate {
  allergen: string;
  severity: AllergySeverity;
  reaction_type?: AllergyReactionType;
  reaction?: string;
  verified_date?: string;
  notes?: string;
}

export interface AllergyUpdate {
  allergen?: string;
  severity?: AllergySeverity;
  reaction_type?: AllergyReactionType | null;
  reaction?: string | null;
  verified_date?: string | null;
  notes?: string | null;
}

export interface DrugCheckConflict {
  allergy_id: string;
  allergen: string;
  severity: AllergySeverity;
  is_life_threatening: boolean;
  reaction?: string | null;
}

export interface DrugCheckResult {
  drug_name: string;
  has_conflict: boolean;
  conflicts: DrugCheckConflict[];
}

// ── API ────────────────────────────────────────────────────────────────────────

export const AllergyAPI = {
  list(patientId: string): Promise<AllergyResponse[]> {
    return apiRequest<AllergyResponse[]>(ROUTES.PATIENT_ALLERGIES(patientId));
  },

  create(patientId: string, data: AllergyCreate): Promise<AllergyResponse> {
    return apiRequest<AllergyResponse>(ROUTES.PATIENT_ALLERGIES(patientId), {
      method: "POST",
      body: JSON.stringify(data),
    });
  },

  update(patientId: string, allergyId: string, data: AllergyUpdate): Promise<AllergyResponse> {
    return apiRequest<AllergyResponse>(ROUTES.PATIENT_ALLERGY(patientId, allergyId), {
      method: "PATCH",
      body: JSON.stringify(data),
    });
  },

  delete(patientId: string, allergyId: string): Promise<null> {
    return apiRequest<null>(
      `${ROUTES.PATIENT_ALLERGY(patientId, allergyId)}?confirm=true`,
      { method: "DELETE" }
    );
  },

  checkDrug(patientId: string, drugName: string): Promise<DrugCheckResult> {
    return apiRequest<DrugCheckResult>(ROUTES.DRUG_CHECK(patientId), {
      method: "POST",
      body: JSON.stringify({ drug_name: drugName }),
    });
  },
};
